import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Flex,
  SearchOption,
  TypeaheadInput,
  AgGrid,
  AgGridReact,
  TypeaheadInputComponent,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { TextWithIcon } from "../../TextWithIcon";
import { filterText } from "../styles_vanilla.css";

export type CustomFilterParams = {
  dependentFilter: string;
};

export type CustomFilterProps = {
  dependentFilter: string;
  options: SearchOption[];
  isLoading: boolean;
  infoText: string;
  hintText: string;
  filterProps: AgGridReact.CustomFilterProps;
  optionsFetcher: (dependentId: string, searchTerm: string) => void;
  onSelection?: (dependentId: string, selection: string) => void;
};

/**
 * When a filter is dependent on the other filter's availability, this component abstracts out the base logic
 * to support that functionality.
 * Usage example: {@link EntityFilter}, {@link AccountFilter}
 * @param filterProps props fed by the AgGrid filtering model
 */
export const CustomFilter = (props: CustomFilterProps) => {
  const {
    dependentFilter,
    filterProps,
    options,
    isLoading,
    infoText,
    hintText,
    optionsFetcher,
    onSelection,
  } = props;
  const { api, getValue, onModelChange } = filterProps;

  const [dependentId, setDependentId] = useState<string>(
    api.getColumnFilterModel(dependentFilter) || "",
  );
  const [filterEnabled, setFilterEnabled] = useState<boolean>(dependentId?.length > 0);

  const componentRef = useRef<TypeaheadInputComponent>(null);

  useEffect(() => {
    componentRef?.current?.clearInputValue();
  }, [dependentId]);

  const handleSelection = useCallback(
    (value?: SearchOption) => {
      if (onSelection) {
        onSelection(dependentId, value?.id as string);
      } else {
        onModelChange(value?.id);
      }
    },
    [dependentId, onModelChange, onSelection],
  );

  const handleDebouncedSearch = useCallback(
    (value?: string) => {
      if (value?.length) {
        optionsFetcher(dependentId, value);
      } else {
        onModelChange(null);
      }
    },
    [dependentId, onModelChange, optionsFetcher],
  );

  const onAnyFilterChanged = useCallback(() => {
    const newId: string = api.getColumnFilterModel(dependentFilter) || "";
    if (newId != dependentId) {
      onModelChange(null);
      setFilterEnabled(newId.length > 0);
      setDependentId(newId);
    }
  }, [api, dependentFilter, dependentId, onModelChange]);

  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      return getValue(params.node) == filterProps.model;
    },
    [filterProps, getValue],
  );

  // register filter callbacks with the grid
  AgGridReact.useGridFilter({ doesFilterPass, onAnyFilterChanged });

  return (
    <Flex flexDirection="column" gap={Sizes.SM}>
      {!filterEnabled && <TextWithIcon label={infoText} iconType={"exclamation-circle"} />}
      <TypeaheadInput
        className={filterText}
        ref={componentRef}
        isClearable
        isFullWidth
        disabled={!filterEnabled}
        isLoading={isLoading}
        options={options}
        onSelectOption={handleSelection}
        onDebouncedSearch={handleDebouncedSearch}
        placeholder={hintText}
      />
    </Flex>
  );
};
